import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';
import { useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { toast } from '@gitbook/spine-react';
import { useEventCallback, useLoadingState } from '@gitbook/util-react-hooks';

import { useSetAnalyticsScreen } from '../../../../navigation';
import { apiClient, apiQuery, useSiteContext } from '../../../../state';
import { VisibilityStatePublic } from '.';
import { VisibilityStateShareLinks } from './VisibilityStateShareLinks';
import {
    DEFAULT_VA_BACKEND,
    VisibilityStateVisitorAuth,
    type VisitorAuthState,
} from './VisibilityStateVisitorAuth';

export const visibilityGroupMap = {
    [api.SiteVisibility.Public]: api.SiteVisibility.Public,
    [api.SiteVisibility.Unlisted]: api.SiteVisibility.Public,
    [api.SiteVisibility.VisitorAuth]: api.SiteVisibility.VisitorAuth,
    [api.SiteVisibility.ShareLink]: api.SiteVisibility.ShareLink,
} as const;

export const visibilityDescriptions = {
    [api.SiteVisibility.Public]: 'Your site is publicly accessible and indexed by search engines.',
    [api.SiteVisibility.Unlisted]:
        'Your site is publicly accessible but not indexed by search engines.',
    [api.SiteVisibility.VisitorAuth]: 'Your site is only accessible to authenticated visitors.',
    [api.SiteVisibility.ShareLink]:
        'Your site is only accessible through specific links that you can revoke at any time.',
} as const;

export function useVisibilityForm() {
    const { organizationId, site, analyticsContext } = useSiteContext();
    const form = useForm({
        defaultValues: {
            visibility: site.visibility,
            group: visibilityGroupMap[site.visibility],
        },
    });

    const formVisibility = form.watch('visibility');
    const formGroup = form.watch('group');

    const { data: visitorAuth } = useSuspenseQuery(
        apiQuery('getSitePublishingAuthById', { organizationId, siteId: site.id })
    );

    const [visitorAuthState, setVisitorAuthState] = React.useState<VisitorAuthState>({
        backend: visitorAuth.backend,
        fallbackURL: visitorAuth.fallbackURL,
    });

    const [updateVisitorAuth, isUpdatingVisitorAuth] = useUpdateVisitorAuth();
    const [updateVisibility, isUpdatingVisibility] = useUpdateVisibility();

    const isUpdating = isUpdatingVisibility || isUpdatingVisitorAuth;

    const visibilityHasChanged = formVisibility !== site.visibility;
    const visitorAuthHasChanged =
        visitorAuthState.fallbackURL !== visitorAuth.fallbackURL ||
        visitorAuthState.backend !== visitorAuth.backend;
    const hasChanged = visibilityHasChanged || visitorAuthHasChanged;

    const save = useEventCallback(async () => {
        if (!hasChanged) {
            return;
        }

        await Promise.all([
            (async () => {
                if (formVisibility === api.SiteVisibility.VisitorAuth && visitorAuthHasChanged) {
                    await updateVisitorAuth(
                        organizationId,
                        site.id,
                        visitorAuthState.backend,
                        visitorAuthState.fallbackURL
                    );
                }
            })(),
            (async () => {
                if (visibilityHasChanged) {
                    await updateVisibility(organizationId, site.id, formVisibility);
                }
            })(),
        ]);
    });

    useSetAnalyticsScreen(
        analytics.createSiteEvent(
            { id: analyticsContext.site, organization: analyticsContext.organization },
            {
                screen: 'site_audience_modal',
                screenTab: formVisibility,
            }
        ),
        [analyticsContext, formVisibility]
    );

    const renderFormGroupDetailsSection = (content: React.ReactNode) => (
        <section className="flex flex-col justify-between gap-6">{content}</section>
    );

    const getFormGroupDetails = () => {
        switch (formGroup) {
            case api.SiteVisibility.Public:
                return renderFormGroupDetailsSection(
                    <VisibilityStatePublic
                        disabled={isUpdating}
                        formVisibility={formVisibility}
                        targetVisibility={formVisibility}
                        setVisibility={(value) => {
                            form.setValue('visibility', value);
                            form.setValue('group', api.SiteVisibility.Public);
                        }}
                    />
                );
            case api.SiteVisibility.ShareLink:
                return renderFormGroupDetailsSection(<VisibilityStateShareLinks />);
            case api.SiteVisibility.VisitorAuth:
                return renderFormGroupDetailsSection(
                    <VisibilityStateVisitorAuth
                        state={visitorAuthState}
                        setState={setVisitorAuthState}
                        integrationName={visitorAuth.integration}
                        privateKey={visitorAuth.privateKey}
                        onSave={save}
                    />
                );
        }
    };

    return { form, formVisibility, hasChanged, save, getFormGroupDetails };
}

function useUpdateVisitorAuth(onDone?: () => void) {
    const [isUpdatingVisitorAuth, wrapLoading] = useLoadingState();

    const updateVisitorAuth = async (
        organizationId: api.Organization['id'],
        siteId: api.Site['id'],
        backend?: api.SitePublishingAuth['backend'],
        fallbackURL?: string
    ) => {
        const normalizedFallbackURL = normalizeFallbackURLParam(fallbackURL);
        const data: api.SitePublishingAuthUpdate = {
            backend: backend ?? DEFAULT_VA_BACKEND,
            ...(backend === 'custom' && typeof normalizedFallbackURL !== 'undefined'
                ? { fallbackURL: normalizedFallbackURL }
                : {}),
        };
        await wrapLoading(async () => {
            await toast.catch(async () => {
                await apiClient.sitePublishingAuth.updateSitePublishingAuthById(
                    organizationId,
                    siteId,
                    data
                );
                toast.success('Audience settings updated');
            }, 'Error while updating authenticated access settings');
        });
        onDone?.();
    };

    return [updateVisitorAuth, isUpdatingVisitorAuth] as const;
}

function useUpdateVisibility(onDone?: () => void) {
    const [isUpdatingVisibility, wrapLoading] = useLoadingState();

    const updateVisibility = async (
        organization: api.Organization['id'],
        site: api.Site['id'],
        visibility: api.SiteVisibility
    ) => {
        await wrapLoading(async () => {
            await toast.catch(async () => {
                await apiClient.sites.updateSiteById(organization, site, { visibility });
                toast.success('Audience settings updated');
            }, 'Error while updating audience settings');
        });
        onDone?.();
    };

    return [updateVisibility, isUpdatingVisibility] as const;
}

function normalizeFallbackURLParam(fallbackURL: string | undefined) {
    if (typeof fallbackURL === 'string') {
        const url = fallbackURL.trim();
        return url.length > 0 ? url : null;
    }

    return undefined;
}
